import React, { createContext, useContext, useMemo } from 'react';
import { useHttpClientCore } from '@/shared';
import { ApiBatchProcessingRepository } from '../repositories/ApiBatchProcessingRepository';
import {
  AnalyzeBatchUseCase,
  AssignBatchUseCase,
  CorrectBundleUseCase,
  SplitBundleUseCase,
  CreateBundleFromUnassignedPagesUseCase,
  DigitizeStoredFileUseCase,
  ExtractBatchUseCase,
  ImportVendorExportUseCase,
  ReconcileVendorImportsUseCase,
  ListVendorDeliveriesUseCase,
  ConfirmAllBundlesUseCase,
  ReExtractAllBatchesUseCase,
  RecountPublishedFindingsUseCase,
  PreviewVendorWorksitesUseCase,
  SaveVendorWorksitesUseCase,
  FetchBatchUseCase,
  GetBatchReviewAuditUseCase,
  GetBundleReviewAuditUseCase,
  GetInProgressQueueUseCase,
  GetInternalUsersUseCase,
  GetOpsReviewQueueUseCase,
  GetReverificationGapsUseCase,
  ListBatchesUseCase,
  ListFollowingRecordsUseCase,
  PublishBatchUseCase,
  LockGroupingUseCase,
  PublishBundleUseCase,
  RevalidateBatchUseCase,
} from '../../application/use-cases/BatchProcessingUseCases';

interface BatchProcessingDIContextType {
  repository: ApiBatchProcessingRepository;
  analyzeBatchUseCase: AnalyzeBatchUseCase;
  extractBatchUseCase: ExtractBatchUseCase;
  importVendorExportUseCase: ImportVendorExportUseCase;
  reconcileVendorImportsUseCase: ReconcileVendorImportsUseCase;
  listVendorDeliveriesUseCase: ListVendorDeliveriesUseCase;
  confirmAllBundlesUseCase: ConfirmAllBundlesUseCase;
  reExtractAllBatchesUseCase: ReExtractAllBatchesUseCase;
  recountPublishedFindingsUseCase: RecountPublishedFindingsUseCase;
  previewVendorWorksitesUseCase: PreviewVendorWorksitesUseCase;
  saveVendorWorksitesUseCase: SaveVendorWorksitesUseCase;
  listBatchesUseCase: ListBatchesUseCase;
  listFollowingRecordsUseCase: ListFollowingRecordsUseCase;
  fetchBatchUseCase: FetchBatchUseCase;
  correctBundleUseCase: CorrectBundleUseCase;
  splitBundleUseCase: SplitBundleUseCase;
  createBundleFromUnassignedPagesUseCase: CreateBundleFromUnassignedPagesUseCase;
  publishBatchUseCase: PublishBatchUseCase;
  revalidateBatchUseCase: RevalidateBatchUseCase;
  getOpsReviewQueueUseCase: GetOpsReviewQueueUseCase;
  getReverificationGapsUseCase: GetReverificationGapsUseCase;
  getBundleReviewAuditUseCase: GetBundleReviewAuditUseCase;
  getBatchReviewAuditUseCase: GetBatchReviewAuditUseCase;
  digitizeStoredFileUseCase: DigitizeStoredFileUseCase;
  assignBatchUseCase: AssignBatchUseCase;
  lockGroupingUseCase: LockGroupingUseCase;
  publishBundleUseCase: PublishBundleUseCase;
  getInProgressQueueUseCase: GetInProgressQueueUseCase;
  getInternalUsersUseCase: GetInternalUsersUseCase;
}

const BatchProcessingDIContext = createContext<BatchProcessingDIContextType | undefined>(undefined);

export const BatchProcessingDIProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const httpClientCore = useHttpClientCore();

  const value = useMemo<BatchProcessingDIContextType>(() => {
    const repository = new ApiBatchProcessingRepository(httpClientCore);
    return {
      repository,
      analyzeBatchUseCase: new AnalyzeBatchUseCase(repository),
      extractBatchUseCase: new ExtractBatchUseCase(repository),
      importVendorExportUseCase: new ImportVendorExportUseCase(repository),
      reconcileVendorImportsUseCase: new ReconcileVendorImportsUseCase(repository),
      listVendorDeliveriesUseCase: new ListVendorDeliveriesUseCase(repository),
      confirmAllBundlesUseCase: new ConfirmAllBundlesUseCase(repository),
      reExtractAllBatchesUseCase: new ReExtractAllBatchesUseCase(repository),
      recountPublishedFindingsUseCase: new RecountPublishedFindingsUseCase(repository),
      previewVendorWorksitesUseCase: new PreviewVendorWorksitesUseCase(repository),
      saveVendorWorksitesUseCase: new SaveVendorWorksitesUseCase(repository),
      listBatchesUseCase: new ListBatchesUseCase(repository),
      listFollowingRecordsUseCase: new ListFollowingRecordsUseCase(repository),
      fetchBatchUseCase: new FetchBatchUseCase(repository),
      correctBundleUseCase: new CorrectBundleUseCase(repository),
      splitBundleUseCase: new SplitBundleUseCase(repository),
      createBundleFromUnassignedPagesUseCase: new CreateBundleFromUnassignedPagesUseCase(
        repository
      ),
      publishBatchUseCase: new PublishBatchUseCase(repository),
      revalidateBatchUseCase: new RevalidateBatchUseCase(repository),
      getOpsReviewQueueUseCase: new GetOpsReviewQueueUseCase(repository),
      getReverificationGapsUseCase: new GetReverificationGapsUseCase(repository),
      getBundleReviewAuditUseCase: new GetBundleReviewAuditUseCase(repository),
      getBatchReviewAuditUseCase: new GetBatchReviewAuditUseCase(repository),
      digitizeStoredFileUseCase: new DigitizeStoredFileUseCase(repository),
      assignBatchUseCase: new AssignBatchUseCase(repository),
      lockGroupingUseCase: new LockGroupingUseCase(repository),
      publishBundleUseCase: new PublishBundleUseCase(repository),
      getInProgressQueueUseCase: new GetInProgressQueueUseCase(repository),
      getInternalUsersUseCase: new GetInternalUsersUseCase(repository),
    };
  }, [httpClientCore]);

  return (
    <BatchProcessingDIContext.Provider value={value}>{children}</BatchProcessingDIContext.Provider>
  );
};

export const useBatchProcessingDIContext = (): BatchProcessingDIContextType => {
  const context = useContext(BatchProcessingDIContext);
  if (!context) {
    throw new Error('useBatchProcessingDIContext must be used within BatchProcessingDIProvider');
  }
  return context;
};
